import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { sortBy } from 'lodash-es';
import { TextInput } from 'ui/TextInput';
import { Multiselect, IMultiselectValueLabelPair } from 'ui/Multiselect';
import FluidButton from 'ui/FluidButton';
import Checkbox from 'ui/Checkbox';
import { BorderWithLabel } from 'ui/BorderWithLabel/BorderWithLabel';
import { enqueueNotification } from 'store/modules/ui';
import { TCurrencyCode } from 'interfaces';
import * as currenciesData from 'config/data/currencies.json';
import {
  IPurchaseOrderSearchDueDate,
  IPurchaseOrderReviewRequest,
} from 'services/BookingManagerApi/types/PurchaseOrdersSearchResponse';
import { IHeadlineLineItemBreakdownExchangeRate } from 'services/BookingManagerApi/types/HeadlineLineItemBreakdown';
import SingleDateInput from 'pureUi/SingleDateInput';
import { PriceAsCentsInput } from 'ui/stateful/PriceAsCentsInput';
import { formatDate } from 'utils';
import { useAvailableCurrencies } from 'hooks/useAvailableCurrencies';
import { InvoiceDueDatesEditor, IInvoiceDueDateRow } from './InvoiceDueDatesEditor';
import { useUploadInvoiceMutation } from '../hooks/useUploadInvoiceMutation';
import { useReviewPurchaseOrderMutation } from '../hooks/useReviewPurchaseOrderMutation';

const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const CurrencyBadge = ({ code, variant }: { code: string; variant: 'yellow' | 'green' }) => {
  const bg = variant === 'yellow' ? 'bg-yellow-advert-light' : 'bg-green-25';
  return (
    <span
      className={`${bg} rounded-[10px] px-[3px] py-[5px] font-pt-sans text-[13px] text-black inline-block w-[36px] text-center`}
    >
      {code}
    </span>
  );
};

const getNewAmountFromPercentageOfOriginal = (totalCents: number, percentage: number): number => {
  return Math.round((totalCents / 100) * percentage);
};

interface IInvoiceFormProps {
  file: File;
  poCurrencySymbol: string;
  poBookingCurrency: TCurrencyCode;
  poBookingTotalCents: number;
  poDueDates: IPurchaseOrderSearchDueDate[];
  exchangeRate: IHeadlineLineItemBreakdownExchangeRate | undefined;
  bookingUuid: string;
  purchaseOrderUuid: string;
  supplierCode: string | null;
}

interface IFormErrors {
  invoiceDate?: string;
  currency?: string;
  totalInvoiceAmount?: string;
  conversionRate?: string;
  description?: string;
  commentText?: string;
}

interface IDueDateErrors {
  date?: string;
  percentage?: string;
  amountCents?: string;
}

const buildInitialDueDates = (poDueDates: IPurchaseOrderSearchDueDate[]): IInvoiceDueDateRow[] => {
  return poDueDates.map(dd => ({
    id: uuidv4(),
    date: dd.date,
    percentage: dd.percentage,
    amountCents: dd.amountCents,
  }));
};

export const InvoiceForm = ({
  file,
  poCurrencySymbol,
  poBookingCurrency,
  poBookingTotalCents,
  poDueDates,
  exchangeRate,
  bookingUuid,
  purchaseOrderUuid,
  supplierCode,
}: IInvoiceFormProps) => {
  const dispatch = useDispatch();
  const { sortedCurrencies: currenciesWithRates } = useAvailableCurrencies();
  const uploadMutation = useUploadInvoiceMutation();
  const reviewMutation = useReviewPurchaseOrderMutation();
  const isSubmitting = uploadMutation.isPending || reviewMutation.isPending;

  const currencyOptions: IMultiselectValueLabelPair[] = useMemo(() => {
    const all = Object.values((currenciesData as unknown) as Record<string, { code: string; name: string }>).filter(
      c => c.code && c.name
    );
    return sortBy(all, c => c.name).map(c => ({
      value: c.code,
      label: `${c.code} - ${c.name}`,
    }));
  }, []);

  const [invoiceDate, setInvoiceDate] = useState<string>('');
  const [currency, setCurrency] = useState<TCurrencyCode | null>(poBookingCurrency);
  const [conversionRate, setConversionRate] = useState<string>('');
  const [totalInvoiceAmountCents, setTotalInvoiceAmountCents] = useState<number | null>(poBookingTotalCents);
  const [vatAmountCents, setVatAmountCents] = useState<number | null>(null);
  const [isVatInvoice, setIsVatInvoice] = useState(false);
  const [description, setDescription] = useState<string>('');
  const [commentText, setCommentText] = useState<string>('');
  const [dueDates, setDueDates] = useState<IInvoiceDueDateRow[]>(() => buildInitialDueDates(poDueDates));

  const isSameCurrency = currency !== null && currency === poBookingCurrency;

  const [formErrors, setFormErrors] = useState<IFormErrors>({});
  const [dueDateErrors, setDueDateErrors] = useState<Record<string, IDueDateErrors>>({});
  const [dueDateSectionErrors, setDueDateSectionErrors] = useState<string[]>([]);

  const clearFormError = useCallback((field: keyof IFormErrors) => {
    setFormErrors(prev => {
      if (!prev[field]) return prev;
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearDueDateError = useCallback(
    (rowId: string, field: keyof { date: string; percentage: string; amountCents: string }) => {
      setDueDateErrors(prev => {
        const rowErrors = prev[rowId];
        if (!rowErrors?.[field]) return prev;
        const { [field]: _, ...restFields } = rowErrors;
        if (Object.keys(restFields).length === 0) {
          const { [rowId]: __, ...restRows } = prev;
          return restRows;
        }
        return { ...prev, [rowId]: restFields };
      });
    },
    []
  );

  // Variance compares the invoice total expressed in booking currency against the PO's
  // booking-currency total. In same-currency mode that's just the Total Amount; in
  // cross-currency mode it's the converted Booking Currency amount (Total / Conversion Rate).
  // Returns `--` when we lack enough data to compute (no total, or cross-currency with no rate).

  // When Total Amount changes, recompute each due-date row's amount from its percentage.
  // Amounts are always in invoice currency (same units as Total Amount the user typed).
  useEffect(() => {
    if (totalInvoiceAmountCents === null) return;
    setDueDates(prev =>
      prev.map(d => ({
        ...d,
        amountCents:
          d.percentage !== null
            ? getNewAmountFromPercentageOfOriginal(totalInvoiceAmountCents, d.percentage)
            : d.amountCents,
      }))
    );
  }, [totalInvoiceAmountCents]);

  const handleCurrencyChange = useCallback(
    (values: string[]) => {
      const newCurrency = values.length > 0 ? (values[0] as TCurrencyCode) : null;

      setCurrency(newCurrency);
      clearFormError('currency');

      if (!newCurrency || newCurrency === poBookingCurrency) {
        setConversionRate('');
        return;
      }
      const currencyData = currenciesWithRates.find(c => c.to.code === poBookingCurrency);
      const rate = currencyData?.rates.find(r => r.from.code === newCurrency);
      setConversionRate(rate ? String(rate.buyRate) : '');
    },
    [clearFormError, poBookingCurrency, currenciesWithRates]
  );

  const bookingCurrencyAmountCents = useMemo(() => {
    if (isSameCurrency) return null;
    if (totalInvoiceAmountCents === null) return null;
    const rate = parseFloat(conversionRate);
    if (!rate || isNaN(rate) || rate === 0) return null;
    return Math.round(totalInvoiceAmountCents * rate);
  }, [isSameCurrency, totalInvoiceAmountCents, conversionRate]);

  const variance = useMemo(() => {
    const totalInBookingCents = isSameCurrency ? totalInvoiceAmountCents : bookingCurrencyAmountCents;
    if (totalInBookingCents === null || poBookingTotalCents === 0) return '--' as const;
    if (totalInBookingCents === poBookingTotalCents) return 'Exact' as const;
    return ((totalInBookingCents - poBookingTotalCents) / poBookingTotalCents) * 100;
  }, [isSameCurrency, totalInvoiceAmountCents, bookingCurrencyAmountCents, poBookingTotalCents]);

  const handleDueDateUpdateDate = useCallback(
    (index: number, date: string) => {
      setDueDates(prev => {
        const updated = [...prev];
        clearDueDateError(updated[index].id, 'date');
        updated[index] = { ...updated[index], date };
        return updated;
      });
    },
    [clearDueDateError]
  );

  const handleDueDateUpdatePercentage = useCallback(
    (index: number, percentage: number | null) => {
      setDueDates(prev => {
        const updated = [...prev];
        clearDueDateError(updated[index].id, 'percentage');
        updated[index] = {
          ...updated[index],
          percentage,
          amountCents:
            percentage !== null && totalInvoiceAmountCents !== null
              ? getNewAmountFromPercentageOfOriginal(totalInvoiceAmountCents, percentage)
              : updated[index].amountCents,
        };
        return updated;
      });
      setDueDateSectionErrors([]);
    },
    [totalInvoiceAmountCents, clearDueDateError]
  );

  const handleDueDateUpdateAmount = useCallback(
    (index: number, amountCents: number | null) => {
      setDueDates(prev => {
        const updated = [...prev];
        clearDueDateError(updated[index].id, 'amountCents');
        updated[index] = { ...updated[index], amountCents };
        return updated;
      });
      setDueDateSectionErrors([]);
    },
    [clearDueDateError]
  );

  const handleRemoveDueDate = useCallback((index: number) => {
    setDueDates(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddDueDate = useCallback(() => {
    setDueDates(prev => [...prev, { id: uuidv4(), date: null, percentage: null, amountCents: null }]);
    setDueDateSectionErrors([]);
  }, []);

  const validate = (requireComment: boolean): boolean => {
    const newFormErrors: IFormErrors = {};
    const newDueDateErrors: Record<string, IDueDateErrors> = {};
    const newSectionErrors: string[] = [];

    if (!invoiceDate) {
      newFormErrors.invoiceDate = 'Field is required';
    }
    if (!currency) {
      newFormErrors.currency = 'Field is required';
    }
    if (totalInvoiceAmountCents === null) {
      newFormErrors.totalInvoiceAmount = 'Field is required';
    }
    if (!isSameCurrency) {
      const rate = parseFloat(conversionRate);
      if (!conversionRate.trim() || isNaN(rate) || rate === 0) {
        newFormErrors.conversionRate = 'Field is required';
      }
    }
    if (!description.trim()) {
      newFormErrors.description = 'Field is required';
    }
    if (requireComment && !commentText.trim()) {
      newFormErrors.commentText = 'Field is required';
    }

    if (dueDates.length === 0) {
      newSectionErrors.push('You must add at least one Due Date');
    }

    dueDates.forEach(row => {
      const rowErrors: IDueDateErrors = {};
      if (!row.date) rowErrors.date = 'Field is required';
      if (row.percentage === null) rowErrors.percentage = 'Field is required';
      if (row.amountCents === null) rowErrors.amountCents = 'Field is required';
      if (Object.keys(rowErrors).length > 0) {
        newDueDateErrors[row.id] = rowErrors;
      }
    });

    if (totalInvoiceAmountCents !== null && dueDates.length > 0 && Object.keys(newDueDateErrors).length === 0) {
      const dueDateSum = dueDates.reduce((sum, row) => sum + (row.amountCents ?? 0), 0);
      if (dueDateSum !== totalInvoiceAmountCents) {
        newSectionErrors.push('Should match Total Invoice Amount');
      }
    }

    setFormErrors(newFormErrors);
    setDueDateErrors(newDueDateErrors);
    setDueDateSectionErrors(newSectionErrors);

    return (
      Object.keys(newFormErrors).length === 0 &&
      Object.keys(newDueDateErrors).length === 0 &&
      newSectionErrors.length === 0
    );
  };

  const buildPayload = (status: 'approved' | 'rejected', invoiceUploadUuid?: string): IPurchaseOrderReviewRequest => {
    const payload: IPurchaseOrderReviewRequest = {
      bookingUuid,
      purchaseOrderUuid,
      status,
      invoiceAmountCents: isSameCurrency ? totalInvoiceAmountCents! : bookingCurrencyAmountCents!,
      invoiceDate,
      invoiceDescription: description.trim() || undefined,
      invoiceDueDates: dueDates.map(dd => ({
        date: dd.date!,
        amountCents: dd.amountCents!,
        percentage: dd.percentage!,
        isConfirmed: false,
      })),
    };

    if (invoiceUploadUuid) {
      payload.invoiceUploadUuid = invoiceUploadUuid;
    }

    if (commentText.trim()) {
      payload.commentText = commentText.trim();
    }

    if (supplierCode) {
      payload.invoiceSupplierCode = supplierCode as string;
    }

    if (!isSameCurrency) {
      payload.invoiceSourceCurrency = currency!;
      payload.invoiceSourceCurrencyAmountCents = totalInvoiceAmountCents!;
      payload.invoiceConversionRate = parseFloat(conversionRate);
    }

    if (vatAmountCents !== null) {
      payload.vatAmountCents = vatAmountCents;
    }
    payload.isTaxInvoiceReceived = isVatInvoice;

    return payload;
  };

  const handleSubmit = async (status: 'approved' | 'rejected') => {
    if (!validate(status === 'rejected')) return;

    try {
      const uploadResult = await uploadMutation.mutateAsync({ file, bookingUuid });
      const payload = buildPayload(status, uploadResult.data.uuid);
      await reviewMutation.mutateAsync(payload);
      const label = status === 'approved' ? 'approved' : 'rejected';
      dispatch(enqueueNotification({ message: `Successfully ${label}`, options: { variant: 'success' } }));
    } catch (error) {
      dispatch(
        enqueueNotification({
          message: 'There was an error submitting your invoice. Please try again later.',
          options: { variant: 'error' },
        })
      );
    }
  };

  const hasDueDateErrors = Object.keys(dueDateErrors).length > 0 || dueDateSectionErrors.length > 0;

  const varianceDisplay = typeof variance === 'number' ? `${formatAmount(variance)}%` : variance;

  return (
    <div className="mt-[15px]">
      {/* Row 1: Date, Description */}
      <div className="flex items-start gap-[20px]">
        <div className="w-[120px] shrink-0">
          <span className="font-pt-sans text-[13px] text-black block mb-[8px]">Date *</span>
          <SingleDateInput
            value={invoiceDate ? new Date(invoiceDate) : null}
            onChange={date => {
              setInvoiceDate(formatDate(date));
              clearFormError('invoiceDate');
            }}
            enablePastDates
            showYearDropdown
            placeholder="Select Date ..."
            buttonClassName={`!h-[35px] !bg-ivory ${
              formErrors.invoiceDate ? '!border-red-95 !bg-red-25' : '!border-gray-40'
            }`}
          />
          {formErrors.invoiceDate && (
            <span className="font-pt-sans text-[13px] text-red-95 block mt-[2px]">{formErrors.invoiceDate}</span>
          )}
        </div>
        <div className="flex-1">
          <span className="font-pt-sans text-[13px] text-black block mb-[8px]">Description *</span>
          <TextInput
            id="invoice-description"
            value={description}
            onChange={e => {
              setDescription(e.currentTarget.value);
              if (e.currentTarget.value.trim()) clearFormError('description');
            }}
            placeholder=""
            className="w-full"
            inputClassName={`!h-[35px] !mt-0 !py-0 !px-[8px] ${
              formErrors.description ? '!border-red-95 !bg-red-25' : ''
            }`}
          />
          {formErrors.description && (
            <span className="font-pt-sans text-[13px] text-red-95 block mt-[2px]">{formErrors.description}</span>
          )}
        </div>
      </div>

      {/* Row 2: Currency, Total Amount, [Conversion + Booking Currency + Variance if cross-currency] */}
      <div className="flex items-start gap-[20px] mt-[15px]">
        <div className="w-[200px] shrink-0">
          <span className="font-pt-sans text-[13px] text-black block mb-[8px]">Currency *</span>
          <Multiselect
            className="bg-ivory"
            itemsClassname="bg-ivory !w-[250px]"
            itemCtaClassName="hover:bg-gray-10"
            options={currencyOptions}
            selectedValues={currency ? [currency] : []}
            onUpdate={handleCurrencyChange}
            isSingleSelectMode
            hideCheckboxes
            isCloseOnSelect
            isIncludeClearButton
            isEnableFuzzySearch
            showSelectedIcon
            selectedValuesRenderer={(label: string) => label.split(' - ')[0]}
          />
          {formErrors.currency && (
            <span className="font-pt-sans text-[13px] text-red-95 block mt-[2px]">{formErrors.currency}</span>
          )}
        </div>
        <div className="w-[120px] shrink-0">
          <span className="font-pt-sans text-[13px] text-black block mb-[8px]">Total Amount *</span>
          <PriceAsCentsInput
            cents={totalInvoiceAmountCents ?? undefined}
            onBlurUpdate={cents => {
              setTotalInvoiceAmountCents(cents ?? null);
              clearFormError('totalInvoiceAmount');
            }}
            disabled={false}
            isAllowUndefined
            className={`w-full h-[35px] border border-solid px-[8px] font-pt-sans text-[15px] text-black ${
              formErrors.totalInvoiceAmount ? 'border-red-95 bg-red-25' : 'border-gray-40 bg-ivory'
            }`}
          />
          {formErrors.totalInvoiceAmount && (
            <span className="font-pt-sans text-[13px] text-red-95 block mt-[2px]">{formErrors.totalInvoiceAmount}</span>
          )}
        </div>
        {!isSameCurrency && currency && (
          <>
            <div className="shrink-0">
              <span className="flex items-center gap-[5px] h-[17px] mb-[8px]">
                <CurrencyBadge code={currency} variant="yellow" />
                <span className="font-pt-sans text-[13px] text-black leading-none">→</span>
                <CurrencyBadge code={poBookingCurrency} variant="green" />
              </span>
              <TextInput
                id="conversion-rate"
                type="number"
                value={conversionRate}
                onChange={e => {
                  setConversionRate(e.currentTarget.value);
                  clearFormError('conversionRate');
                }}
                className="w-full"
                inputClassName={`!h-[35px] !mt-0 !py-0 !px-[8px] ${
                  formErrors.conversionRate ? '!border-red-95 !bg-red-25' : ''
                }`}
              />
              {formErrors.conversionRate && (
                <span className="font-pt-sans text-[13px] text-red-95 block mt-[2px]">{formErrors.conversionRate}</span>
              )}
            </div>
            <div className="shrink-0">
              <span className="font-pt-sans text-[13px] text-black block mb-[8px]">Booking Currency</span>
              <span className="font-pt-sans text-[15px] text-black block h-[35px] leading-[35px] px-[10px] border border-solid border-gray-40 bg-gray-10 min-w-[100px]">
                {bookingCurrencyAmountCents !== null ? formatAmount(bookingCurrencyAmountCents / 100) : '---'}
              </span>
            </div>
            <div className="shrink-0">
              <span className="font-pt-sans text-[13px] text-black block mb-[8px]">Variance</span>
              <span
                className={`font-pt-sans text-[15px] block pt-[6px] ${
                  variance === 'Exact' ? 'text-green-100' : 'text-black'
                }`}
              >
                {varianceDisplay}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Row 3: VAT Amount, TAX Invoice */}
      <div className="flex items-end gap-[20px] mt-[15px]">
        <div className="w-[120px] shrink-0">
          <span className="font-pt-sans text-[13px] text-black block mb-[8px]">VAT Amount</span>
          <PriceAsCentsInput
            cents={vatAmountCents ?? undefined}
            onBlurUpdate={cents => setVatAmountCents(cents ?? null)}
            disabled={false}
            isAllowUndefined
            className="w-full h-[35px] border border-solid border-gray-40 bg-ivory px-[8px] font-pt-sans text-[15px] text-black"
          />
        </div>
        <div className="shrink-0 pb-[8px]">
          <label className="font-pt-sans flex items-center text-black text-[13px] gap-[6px] cursor-pointer">
            <Checkbox
              checked={isVatInvoice}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsVatInvoice(e.target.checked)}
            />
            <span>TAX Invoice</span>
          </label>
        </div>
      </div>

      {/* Due Dates */}
      <div className="mt-[25px]">
        <BorderWithLabel label="Due Dates" wrappingError={hasDueDateErrors ? ' ' : undefined}>
          <div className="px-[15px]">
            <InvoiceDueDatesEditor
              dueDates={dueDates}
              errors={dueDateErrors}
              sectionErrors={dueDateSectionErrors}
              onUpdateDate={handleDueDateUpdateDate}
              onUpdatePercentage={handleDueDateUpdatePercentage}
              onUpdateAmount={handleDueDateUpdateAmount}
              onRemoveRow={handleRemoveDueDate}
              onAddRow={handleAddDueDate}
            />
          </div>
        </BorderWithLabel>
      </div>

      {/* Comments */}
      <div className="mt-[15px]">
        <span className="font-pt-sans text-[13px] text-black block mb-[8px]">Comment</span>
        <TextInput
          id="invoice-comment"
          value={commentText}
          onChange={e => {
            setCommentText(e.currentTarget.value);
            if (e.currentTarget.value.trim()) clearFormError('commentText');
          }}
          placeholder=""
          className="w-full"
          inputClassName={`!h-[35px] !mt-0 !py-0 !px-[8px] ${
            formErrors.commentText ? '!border-red-95 !bg-red-25' : ''
          }`}
        />
        {formErrors.commentText && (
          <span className="font-pt-sans text-[13px] text-red-95 block mt-[2px]">{formErrors.commentText}</span>
        )}
      </div>

      {/* Approve / Reject */}
      <div className="flex items-center gap-[15px] mt-[25px]">
        <FluidButton
          type="secondary"
          onClick={() => handleSubmit('rejected')}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Reject Invoice
        </FluidButton>
        <FluidButton
          type="primary"
          onClick={() => handleSubmit('approved')}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Approve Invoice
        </FluidButton>
      </div>
    </div>
  );
};
